/**
 * EXPO → Dosyalar top-level page.
 *
 * Mirror of the COP Dosyalar module for EXPO tenants. Two workspaces
 * (folder tree + file grid + upload dialog + preview modal) share the same
 * UI (`SharedFilesView`), driven by different API adapters:
 *   - "contacts" tab (Kişisel Dosyalar) → `personalFilesApi` (per-user)
 *   - "shared"   tab (Ortak Dosyalar)  → `sharedFilesApi`   (tenant-wide)
 * plus the tenant-wide "Görev Ekleri" list. (İnşaat tab is COP-only.)
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Contact2, FolderOpen, Paperclip } from "lucide-react";
import { personalFilesApi, sharedFilesApi } from "../../lib/copApi";
import SharedFilesView from "../cop/SharedFilesView";
import SharedFilePreviewModal from "../cop/SharedFilePreviewModal";
import TaskAttachmentsView from "../cop/TaskAttachmentsView";

export default function ExpoFiles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab =
    tabParam === "shared"
      ? "shared"
      : tabParam === "task-attachments"
        ? "task-attachments"
        : "contacts";
  const [tab, setTab] = useState(initialTab);
  const previewFileId = searchParams.get("file");
  const [deepLinkedFile, setDeepLinkedFile] = useState(null);

  useEffect(() => {
    if (tab === "shared" && previewFileId) {
      sharedFilesApi
        .getMeta(previewFileId)
        .then((doc) => setDeepLinkedFile(doc))
        .catch(() => {
          toast.error("Dosya bulunamadı veya erişim yok");
        });
    }
  }, [tab, previewFileId]);

  const switchTab = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === "contacts") params.delete("tab");
    else params.set("tab", next);
    if (next !== "shared") params.delete("file");
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="expo-files-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          EXPO
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
          Dosyalar
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          {tab === "contacts"
            ? "Kişisel dosya arşivin — yalnızca senin görebildiğin klasörler ve dosyalar."
            : tab === "shared"
              ? "Tenant'ının ortak arşivi — klasörle organize et, şifreyle koru, ekiple paylaş."
              : "Görevlerine eklenen tüm dosyalar tek listede — filename, görev veya proje ile ara."}
        </p>
      </div>

      <div
        className="inline-flex rounded-lg border border-zinc-200 bg-white p-1 shadow-sm"
        role="tablist"
        data-testid="expo-files-tabbar"
      >
        <TabButton
          active={tab === "contacts"}
          onClick={() => switchTab("contacts")}
          Icon={Contact2}
          label="Kişisel Dosyalar"
          testid="expo-files-tab-contacts"
        />
        <TabButton
          active={tab === "shared"}
          onClick={() => switchTab("shared")}
          Icon={FolderOpen}
          label="Ortak Dosyalar"
          testid="expo-files-tab-shared"
        />
        <TabButton
          active={tab === "task-attachments"}
          onClick={() => switchTab("task-attachments")}
          Icon={Paperclip}
          label="Görev Ekleri"
          testid="expo-files-tab-task-attachments"
        />
      </div>

      {tab === "shared" ? (
        <SharedFilesView
          key="shared"
          api={sharedFilesApi}
          initialFolderId={searchParams.get("folder")}
        />
      ) : tab === "task-attachments" ? (
        <TaskAttachmentsView />
      ) : (
        <SharedFilesView key="personal" api={personalFilesApi} />
      )}

      {deepLinkedFile && (
        <SharedFilePreviewModal
          file={deepLinkedFile}
          onClose={() => {
            setDeepLinkedFile(null);
            const params = new URLSearchParams(searchParams);
            params.delete("file");
            setSearchParams(params, { replace: true });
          }}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, Icon, label, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
        active
          ? "bg-zinc-900 text-white shadow-sm"
          : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
